import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { capitalize, defaultTo, get } from 'lodash';
import styled from '@emotion/styled';
import { N500 } from '@atlaskit/theme/colors';
import Button from '@atlaskit/button';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import { PromotionPartnerTier } from '@atlassian/cssxp-ccp-types';
import LinkExternalIcon from '@atlaskit/icon/utility/link-external';
import { Maybe, TermDetailsAccountConnection, TermPaymentTypes } from '@css-xp/graphtypes/src/generated-types-new';
import { getRouteUrl, MoreActions, MoreActionsItem, useApplicationContext } from '@css-xp/base';
import { routePaths } from '@css-xp/ppc-constants';
import { FormatCurrency, Net30StatusBanner, useNet30ApplicationStatus, TermPaymentMethod } from '@css-xp/ppc-common';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { getCreditUsageUiUrlViaIdentity } from '@css-xp/ppc-navigation';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import './deferred-payment.scss';
import { container, outstandingBalance } from './deferred-payment.styles';

const StyledHigherPaymentTerms = styled.div`
  > button {
    color: ${N500} !important;
  }
`;

interface deferredPaymentMethodArgs {
  creditLimit: Maybe<number> | undefined;
  paymentMethod: Maybe<string> | undefined;
  txaId: string;
  billingAccountsList?: Maybe<TermDetailsAccountConnection> | undefined;
  hasPayByStatement?: boolean;
  invoiceDueDays?: Maybe<number> | undefined;
  version?: number;
  isLoading?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
}

const LoadingValueIndicator = () => (
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="200px" />
  </SkeletonLoaderWrapper>
);

export const DeferredPayment = ({
  txaId,
  creditLimit,
  paymentMethod,
  billingAccountsList,
  hasPayByStatement,
  invoiceDueDays,
  version,
  isLoading,
  isMultiCurrencyChangesEnabled,
}: deferredPaymentMethodArgs) => {
  const { isInProgress, openTicketNumber } = useNet30ApplicationStatus({ txaId, paymentMethod });
  const isNet14PaymentMethod = paymentMethod === TermPaymentTypes.NET14 || invoiceDueDays === 14;
  const isNet30PaymentMethod = paymentMethod === TermPaymentTypes.NET30 || invoiceDueDays === 30;
  const totalCreditLimit = isMultiCurrencyChangesEnabled ? billingAccountsList?.totalCreditLimit : creditLimit;

  const availableCreditLimit = defaultTo(get(billingAccountsList, 'availableCreditLimit'), 0);
  const currency = billingAccountsList?.currency;
  const currentTermAccountStatus = billingAccountsList?.status;
  const pendingInvoicesTotal = billingAccountsList?.nodes?.reduce(
    (acc, obj) => acc + (obj?.pendingInvoiceRequestsTotal ?? 0),
    0
  );
  const pendingInvoiceRequestsTotal = defaultTo(pendingInvoicesTotal, 0);
  const actualAvailableCreditLimit = availableCreditLimit - pendingInvoiceRequestsTotal;
  const currentAccount = get(
    billingAccountsList?.nodes?.filter((node) => node?.transactionAccountId === txaId),
    '0'
  );
  const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : paymentMethod;

  const { email } = useApplicationContext();

  const renderViewDetails = () => {
    const creditDetailsUrl = getRouteUrl(routePaths.creditDetails, { txaId });

    if (version === 2) {
      return (
        <Link to={creditDetailsUrl} data-testid="view-details-link" aria-label="View pay on account details">
          View details
        </Link>
      );
    }

    return (
      <div className="view-details-container" data-testid="view-details">
        <Link to={creditDetailsUrl}>
          <Button>View details</Button>
        </Link>
      </div>
    );
  };

  const renderHigherPaymentMethodsCTA = () => {
    return (
      <StyledHigherPaymentTerms>
        <Button
          onClick={() => {
            window.open(getCreditUsageUiUrlViaIdentity({ transactionAccountId: txaId, email }));
          }}
          iconAfter={<LinkExternalIcon label="" />}
        >
          Apply for higher payment terms
        </Button>
      </StyledHigherPaymentTerms>
    );
  };

  const renderPayByInvoice = () => {
    return (
      <Flex justifyContent="space-between" alignItems="center" gap="space.200">
        <Box paddingInlineEnd="space.200" xcss={outstandingBalance}>
          <Stack space="space.050">
            <Text weight="bold">Outstanding balance</Text>
            <Text color="color.text.subtle" align="end">
              {isLoading ? (
                <LoadingValueIndicator />
              ) : (
                <FormatCurrency
                  value={currentAccount?.creditOutstanding}
                  currency={currency}
                  showCurrencyCode
                  showCommas
                />
              )}
            </Text>
          </Stack>
        </Box>
        <Stack space="space.050">
          <Text weight="bold">Available credit</Text>
          <Text color="color.text.subtle" align="end">
            {isLoading ? (
              <LoadingValueIndicator />
            ) : (
              <FormatCurrency
                value={actualAvailableCreditLimit.toFixed(2)}
                currency={currency}
                showCurrencyCode
                showCommas
              />
            )}
          </Text>
        </Stack>
      </Flex>
    );
  };

  const renderTermPaymentType = () => {
    return (
      <TermPaymentMethod
        paymentNetType={hasPayByStatement ? '' : (termPaymentType as string)}
        totalLimit={hasPayByStatement ? undefined : (totalCreditLimit as number)}
        currentTermAccountStatus={currentTermAccountStatus}
        hasPayOnAccount={!!hasPayByStatement}
        version={version}
        currency={currency}
        isLoading={isLoading}
      />
    );
  };

  const moreActions: MoreActionsItem[] = [
    {
      id: 'view-details',
      content: 'View details',
      predicate: !hasPayByStatement && version === 2,
      action: () => window.open(getRouteUrl(routePaths.creditDetails, { txaId })),
    },
  ];

  const renderMoreActions = () => {
    return (
      <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD}>
        <MoreActions actions={moreActions} testId="payment-terms-actions-btn" />
      </AdvocateAccessWall>
    );
  };

  const renderCreditCheckInProgressBanner = () => {
    return <Net30StatusBanner isInProgress={isInProgress} openTicketNumber={openTicketNumber} version={version} />;
  };

  if (version === 2) {
    return (
      <>
        {renderCreditCheckInProgressBanner()}
        <Box xcss={container} padding="space.200">
          <Flex justifyContent={hasPayByStatement ? 'start' : 'space-between'} alignItems="center">
            {renderTermPaymentType()}
            <Flex gap="space.200" alignItems="center">
              {!hasPayByStatement && renderPayByInvoice()}
              {hasPayByStatement && (
                <>
                  <Text>
                    Credit used by this billing account:
                    <FormatCurrency
                      value={currentAccount?.creditOutstanding}
                      currency={currency}
                      showCurrencyCode
                      showCommas
                    />
                  </Text>
                  {renderViewDetails()}
                </>
              )}
              {renderMoreActions()}
            </Flex>
          </Flex>
        </Box>
      </>
    );
  }

  return (
    <>
      <div className="ppc-payment-method-line-item" data-testid="ppc-deferred-payment-line-items">
        <div
          className={classNames('ppc-deferred-payment-method-panel-container', { 'pay-on-account': hasPayByStatement })}
        >
          {renderTermPaymentType()}
        </div>
        {isLoading ? (
          <LoadingValueIndicator />
        ) : (
          <div>
            {}
            {hasPayByStatement || (
              <p className="mb-8">
                Available credit:{' '}
                <strong>
                  <FormatCurrency
                    value={actualAvailableCreditLimit.toFixed(2)}
                    currency={currency}
                    showCurrencyCode
                    showCommas
                  />
                </strong>
                {pendingInvoiceRequestsTotal !== 0 && (
                  <>
                    {' '}
                    (adjusted for inflight invoices worth{' '}
                    <strong>
                      <FormatCurrency
                        value={pendingInvoiceRequestsTotal}
                        currency={currency}
                        showCurrencyCode
                        showCommas
                      />
                    </strong>
                    )
                  </>
                )}
              </p>
            )}
            <p>
              {hasPayByStatement ? `Credit used by this billing account: ` : `Credit used: `}
              <strong>
                <FormatCurrency
                  value={currentAccount?.creditOutstanding}
                  currency={currency}
                  showCurrencyCode
                  showCommas
                />
              </strong>
            </p>
          </div>
        )}
        <div className="deferred-payment-more-actions">
          {renderViewDetails()}
          {!hasPayByStatement && renderHigherPaymentMethodsCTA()}
        </div>
      </div>
      {renderCreditCheckInProgressBanner()}
    </>
  );
};
